import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
// Hooks
import useDialog from 'hooks/useDialog';
import useMenu from 'hooks/useMenu';
// Async
import PropertyOwnershipsAsync from 'store/propertyOwnerships/propertyOwnershipsAsync';
// Models
import IPropertyOwnership from 'models/PropertyOwnership';
// Types
import PropertyOwnershipStatus from 'types/PropertyOwnershipStatus';
// MUI
import { makeStyles } from '@mui/styles';
import {
  Box, Card, Chip, Collapse, Divider, Grid, Tooltip,
  IconButton, Typography
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  EventAvailableOutlined as EventAvailableOutlinedIcon,
  EventBusyOutlined as EventBusyOutlinedIcon,
  LoopOutlined as LoopOutlinedIcon,
  ReportGmailerrorred as ReportGmailerrorredIcon,
} from '@mui/icons-material';
// Components
import Collapsable from 'components/Collapsable';
import MyPropertyOwnershipsForm from './MyPropertyOwnerships.form';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
// Utilities
import { formatStartEndDate } from 'utilities/DateTimeFormatter';
import { formatCash, getContent } from 'utilities/Utilities';

type Props = {
  propertyOwnership: IPropertyOwnership,
};

const MyPropertyOwnershipCard:React.FC<Props> = ({ propertyOwnership }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const urlPhoto = propertyOwnership.images ? propertyOwnership.images[0].url : '/img/noPropertyFound.png';

  const { Menu, MenuItem, openMenu } = useMenu();
  const { Dialog, openDialog, closeDialog } = useDialog();

  // handle remove modal
  const [openRemoveModal, setOpenRemoveModal] = useState<boolean>(false);
  const handleOpenRemoveModal = () => setOpenRemoveModal(prev => !prev);

  // handle extended
  const [ extended, setExtended ] = useState<boolean>(false);
  const toggleExtended = () => setExtended(prev => !prev);

  const handleRemovePropertyOwnership = () => {
    dispatch(PropertyOwnershipsAsync.deletePropertyOwnerships(propertyOwnership.id));
  }

  return (
    <React.Fragment>
      <ConfirmDeleteModal
        open={openRemoveModal}
        onClose={handleOpenRemoveModal}
        handleRemove={handleRemovePropertyOwnership}
        title={getContent('labels').labelTimeshareSingularText}
      />

      <Dialog maxWidth="sm">
        <MyPropertyOwnershipsForm onClose={closeDialog} propertyOwnership={propertyOwnership} />
      </Dialog>

      <Card sx={{ p: 2, borderRadius: 0, backgroundColor: extended ? 'rgba(0, 0, 0, 0.04)' : '#fff', borderBottom: '1px solid #eee', width: '100%' }}>
        <Collapsable onClick={toggleExtended}>
          <Grid container spacing={2}>
            <Grid item xs={10} md={4} sx={{ display: 'flex', alignItems: 'center', gap: '5px', order: { xs: -2, md: 0 } }}>
              {!propertyOwnership.verified && (
                <Tooltip title="Is being checked">
                  <ReportGmailerrorredIcon />
                </Tooltip>
              )}
              <Typography className={classes.text}>
                {propertyOwnership.property.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={5} alignSelf="center">
              <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: {xs:2, sm:0} }}>
                <Typography className={classes.title} sx={{ display: {xs: 'block', sm: 'none' } }}>
                  Date
                </Typography>
                <Typography className={classes.text}>
                  {formatStartEndDate(propertyOwnership.start, propertyOwnership.end)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography className={classes.title} sx={{ display: {xs: 'block', sm: 'none' } }}>
                  Status
                </Typography>
                {propertyOwnership.status === PropertyOwnershipStatus.Available && (
                  <Chip color={'success'} size="small" label="Available" icon={<EventAvailableOutlinedIcon />} />
                )}
                {propertyOwnership.status === PropertyOwnershipStatus.Pending && (
                  <Chip color={'info'} size="small" label="Pending" icon={<LoopOutlinedIcon />} />
                )}
                {propertyOwnership.status === PropertyOwnershipStatus.Unavailable && (
                  <Chip color={'error'} size="small" label="Unavailable" icon={<EventBusyOutlinedIcon />} />
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex',  alignSelf: 'center', justifyContent: 'space-between', flexDirection: {xs:'row', sm:'column'} }}>
              <Typography className={classes.title}>
                Price
              </Typography>
              <Typography className={classes.text}>
                {formatCash(propertyOwnership.price)}
              </Typography>
            </Grid>
            {propertyOwnership.status === PropertyOwnershipStatus.Available && (
              <Grid item xs={2} md={1} className={classes.actions} alignSelf="center" sx={{ order: { xs: -2, md: 0 } }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Tooltip title="Actions">
                    <IconButton onClick={openMenu}>
                      <MoreVertIcon />
                    </IconButton>
                  </Tooltip>
                  <Menu>
                    <MenuItem onClick={openDialog}>{`Edit ${getContent('labels').labelTimeshareSingularText}`}</MenuItem>
                    <MenuItem onClick={handleOpenRemoveModal} sx={{ color: '#D32F2F' }}>{`Delete ${getContent('labels').labelTimeshareSingularText}`}</MenuItem>
                  </Menu>
                </div>
              </Grid>
            )}
          </Grid>
        </Collapsable>
        <Collapse in={extended}>
          <Divider sx={{ p: 1 }} />
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <img src={urlPhoto} alt={'timeshare'} className={classes.img} />
            </Grid>
            <Grid item xs={12} sm={6} md={5}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: {xs: 'row', sm: 'column'} }}>
                  <Typography className={classes.title}>
                    View
                  </Typography>
                  <Typography className={classes.text}>
                    {propertyOwnership.view}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: {xs: 'row', sm: 'column'} }}>
                  <Typography className={classes.title}>
                    Country
                  </Typography>
                  <Typography className={classes.text}>
                    {propertyOwnership.property.country}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ pt: 2 }}>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: {xs: 'row', sm: 'column'} }}>
                  <Typography className={classes.title}>
                    Bedrooms/Bathrooms
                  </Typography>
                  <Typography className={classes.text}>
                    {`${propertyOwnership.bedrooms}/${propertyOwnership.bathrooms}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: {xs: 'row', sm: 'column'} }}>
                  <Typography className={classes.title}>
                    Region
                  </Typography>
                  <Typography className={classes.text}>
                    {propertyOwnership.property.region}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography className={classes.title}>
                Notes
              </Typography>
              <Typography className={classes.text}>
                {propertyOwnership.ownerNotes || '-'}
              </Typography>
            </Grid>
          </Grid>
        </Collapse>
      </Card>
    </React.Fragment>
  );
}

export default MyPropertyOwnershipCard;

const useStyles = makeStyles({
  title: {
    fontSize: '12px',
    lineHeight: '166%',
    letterSpacing: '0.4px',
    color: 'rgba(0, 0, 0, 0.6)',
  },
  text: {
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  link: {
    color: '#1976D2',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});
